/**
 * React component for Jodit HTML editor
 *  + Jodit module lazy loading support.
 *  + Debounce for change event to improve performance
 */
import React, {
  useEffect, useRef, forwardRef, useLayoutEffect, useMemo,
} from 'react';
import {
  func, number, string, object,
} from 'prop-types';
import 'jodit/build/jodit.min.css';

let CHANGE_DEBOUNCE_FLAG;

const HTMLEditor = forwardRef(({
  onChange, onBlur, tabIndex, name, value, config,
}, ref) => {
  const textArea = useRef(null);

  // jodit module loader on component mount
  const JoditLoader = useMemo(() => new Promise((resolve) => {
    import('jodit').then((res) => resolve(res));
  }), []);

  function blurHandler(newContent, e) {
    if (onBlur && typeof onBlur === 'function') {
      onBlur(newContent, e);
    }
  }

  function changeHandler(newContent) {
    if (CHANGE_DEBOUNCE_FLAG) clearTimeout(CHANGE_DEBOUNCE_FLAG);

    CHANGE_DEBOUNCE_FLAG = setTimeout(() => {
      if (onChange && typeof onChange === 'function') {
        onChange(newContent);
      }
    }, 300);
  }

  // set the value of forwarded ref
  useLayoutEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        ref(textArea.current);
      } else {
        ref.current = textArea.current;
      }
    }
  }, [textArea]);

  // create instance of jodit
  useEffect(() => {
    const element = textArea.current;

    (async () => {
      const Jodit = await JoditLoader;

      const configData = {
        // buttons: 'bold,strikethrough,underline,italic,eraser,|,superscript,subscript,|,ul,ol,|,outdent,indent,|,font,fontsize,brush,paragraph,|,table,link,image,|,align,,undo,redo,|,dots,strikethrough,underline,,selectall,cut,copy,paste,symbol,print,fullsize',
        toolbarAdaptive: false,
        ...config,
      };
      textArea.current = Jodit.Jodit.make(element, configData);
      textArea.current.workplace.tabIndex = tabIndex || -1;

      // textArea.current.value = defaultValue;
      textArea.current.events.on('blur', (e) => blurHandler(textArea.current.value, e));
      textArea.current.events.on('change', (value) => changeHandler(value));
    })();

    return () => {
      if (textArea?.current) {
        textArea.current.destruct();
      }

      textArea.current = element;
    };
  }, []);

  // useEffect(() => {
  //   if (textArea && textArea.current) {
  //     textArea.current.value = defaultValue;
  //   }
  // }, [textArea, defaultValue]);

  useEffect(() => {
    if (textArea?.current?.value !== value) {
      textArea.current.value = value;
    }
  }, [value]);

  return <textarea ref={textArea} name={name} />;
});

HTMLEditor.propTypes = {
	config: object,
  name: string,
  tabIndex: number,
  onChange: func,
  onBlur: func,
  value: string,
};

export default HTMLEditor;
